/****************************************************************************
 * COPYRIGHT (c) 2024 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/
import APIManager from "../../ApiManager/ApiManager";
import {
  FETCH_COUNTRY_DETAILS_REQUEST,
  FETCH_COUNTRY_DETAILS_SUCCESS,
  FETCH_COUNTRY_DETAILS_FAILURE,
} from "./CountryDetailsTypes";
import { COMMON_ERROR } from "../ApplicationDetailsError/ApplicationDetailsErrorTypes";

const CACHE_CONFIG = {
  key: 'countries-cache',
  timestampKey: 'countries-cache-timestamp',
  durationMs: 5 * 60 * 1000, // 5 minutes
};

// Utility function to safely get and validate cache
const getCacheData = (cacheKey, timestampKey, durationMs) => {
  if (typeof sessionStorage === 'undefined') return null;

  try {
    const cached = sessionStorage.getItem(cacheKey);
    const timestamp = sessionStorage.getItem(timestampKey);
    
    if (!cached || !timestamp) return null;

    // Check if cache has expired
    const cacheTime = Number.parseInt(timestamp, 10);
    if (Date.now() - cacheTime > durationMs) {
      sessionStorage.removeItem(cacheKey);
      sessionStorage.removeItem(timestampKey);
      return null;
    }

    const parsed = JSON.parse(cached);
    return Array.isArray(parsed) ? parsed : null;
  } catch (error) {
    console.warn('Failed to parse cache', error);
    return null;
  }
};

// Utility function to safely set cache with timestamp
const setCacheData = (cacheKey, timestampKey, data) => {
  if (typeof sessionStorage === 'undefined') return;

  try {
    sessionStorage.setItem(cacheKey, JSON.stringify(data));
    sessionStorage.setItem(timestampKey, Date.now().toString());
  } catch (error) {
    console.warn('Failed to persist cache', error);
  }
};

export const fetchCountriesDetailsRequest = () => {
  return {
    type: FETCH_COUNTRY_DETAILS_REQUEST,
  };
};

export const fetchCountriesDetailsSuccess = (countriesData) => {
  return {
    type: FETCH_COUNTRY_DETAILS_SUCCESS,
    payload: countriesData,
  };
};

export const fetchCountriesDetailsFailure = (error) => {
  return {
    type: FETCH_COUNTRY_DETAILS_FAILURE,
    payload: error,
  };
};

export const fetchCountriesDetails = () => {
  return async function (dispatch) {
    // Check cache first
    const cachedCountries = getCacheData(
      CACHE_CONFIG.key,
      CACHE_CONFIG.timestampKey,
      CACHE_CONFIG.durationMs
    );
    if (cachedCountries && cachedCountries.length > 0) {
      dispatch(fetchCountriesDetailsSuccess(cachedCountries));
      return cachedCountries;
    }

    dispatch(fetchCountriesDetailsRequest());
    try {
      const data = await APIManager.getCountries();
      setCacheData(CACHE_CONFIG.key, CACHE_CONFIG.timestampKey, data);
      dispatch(fetchCountriesDetailsSuccess(data));
    } catch (error) {
      dispatch(fetchCountriesDetailsFailure(error));
      dispatch(commonError(error, FETCH_COUNTRY_DETAILS_FAILURE));
    }
  };
};

export const commonError=(error,errorType)=>{
  return({
    type:COMMON_ERROR,
    payload:{
      message:error.message,
      errorType:errorType
    }
  })
}
